import {
  mockInventoryItems,
  mockInventoryTransactions,
  mockInventoryCategories,
} from '../../__mocks__/administration';
import { createHttpService } from '../ApiService';
import { normalizePaginatedResponse } from '../__shared/paginationUtils';
import {
  TInventoryItemListApiResponse,
  TInventoryTransactionListApiResponse,
  TInventoryCategoryListApiResponse,
  TInventoryItemParams,
  TInventoryTransactionParams,
  TInventoryCategoryParams,
  TInventoryItem,
  TInventoryTransaction,
  TInventoryCategory,
} from './inventory.api.interfaces';
import isAppWithMock from 'src/utils/isAppWithMock';

export const useInventoryService = () => {
  const httpAPI = createHttpService();
  const baseInventoryPath = '/inventory';
  const withMockData = isAppWithMock();

  // ===================== INVENTORY ITEMS =====================

  const getInventoryItemList = (
    _params: TInventoryItemParams
  ): Promise<TInventoryItemListApiResponse> => {
    if (withMockData) {
      // Apply filtering for mock data
      let filteredItems = [...mockInventoryItems];

      if (_params.category) {
        filteredItems = filteredItems.filter(
          (item) => item.category === _params.category
        );
      }

      if (_params.isActive !== undefined) {
        filteredItems = filteredItems.filter(
          (item) => item.isActive === _params.isActive
        );
      }

      if (_params.search) {
        const searchTerm = _params.search.toLowerCase();
        filteredItems = filteredItems.filter(
          (item) =>
            item.itemName.toLowerCase().includes(searchTerm) ||
            item.description.toLowerCase().includes(searchTerm) ||
            item.supplier.toLowerCase().includes(searchTerm)
        );
      }

      if (_params.location) {
        filteredItems = filteredItems.filter((item) =>
          item.location.toLowerCase().includes(_params.location!.toLowerCase())
        );
      }

      // Pagination
      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 10;
      const startIndex = (page - 1) * limit;
      const paginatedItems = filteredItems.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedItems, count: filteredItems.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.category && { category: _params.category }),
        ...(_params.subCategory && { subCategory: _params.subCategory }),
        ...(_params.location && { location: _params.location }),
        ...(_params.isActive !== undefined && { isActive: _params.isActive }),
        ...(_params.search && { search: _params.search }),
        ...(_params.sort && { sort: _params.sort }),
        ...(_params.order && { order: _params.order }),
      },
    };

    return httpAPI.admin
      .get<TInventoryItemListApiResponse>(
        `${baseInventoryPath}/items`,
        requestObj
      )
      .then((res) => {
        const body = res?.data ?? res;
        if (body && typeof body === 'object' && (Array.isArray((body as any).data) || (body as any).data === undefined)) {
          return normalizePaginatedResponse<TInventoryItem>(body as any, 'data');
        }
        return normalizePaginatedResponse<TInventoryItem>({}, 'data');
      })
      .catch((error) => {
        console.error('Error fetching inventory items:', error);
        return normalizePaginatedResponse<TInventoryItem>({}, 'data');
      });
  };

  const getInventoryItemById = (id: string): Promise<TInventoryItem> => {
    if (withMockData) {
      const item = mockInventoryItems.find((item) => item.id === id);
      if (item) {
        return Promise.resolve(item);
      }
      return Promise.reject(new Error('Item not found'));
    }

    return httpAPI.admin.get<TInventoryItem>(
      `${baseInventoryPath}/items/${id}`
    );
  };

  const createInventoryItem = (
    item: Omit<TInventoryItem, 'id' | 'totalValue'>
  ): Promise<TInventoryItem> => {
    if (withMockData) {
      const newItem: TInventoryItem = {
        ...item,
        id: `item-${Date.now()}`,
        totalValue: item.quantity * item.unitPrice,
      };
      return Promise.resolve(newItem);
    }

    return httpAPI.admin.post<TInventoryItem>(
      `${baseInventoryPath}/items`,
      item
    );
  };

  const updateInventoryItem = (
    id: string,
    item: Partial<TInventoryItem>
  ): Promise<TInventoryItem> => {
    if (withMockData) {
      const existingItem = mockInventoryItems.find((i) => i.id === id);
      if (existingItem) {
        const updatedItem = { ...existingItem, ...item };
        if (item.quantity !== undefined || item.unitPrice !== undefined) {
          updatedItem.totalValue = updatedItem.quantity * updatedItem.unitPrice;
        }
        return Promise.resolve(updatedItem);
      }
      return Promise.reject(new Error('Item not found'));
    }

    return httpAPI.admin.put<TInventoryItem>(
      `${baseInventoryPath}/items/${id}`,
      item
    );
  };

  const deleteInventoryItem = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(`${baseInventoryPath}/items/${id}`);
  };

  // ===================== INVENTORY TRANSACTIONS =====================

  const getInventoryTransactionList = (
    _params: TInventoryTransactionParams
  ): Promise<TInventoryTransactionListApiResponse> => {
    if (withMockData) {
      let filteredTransactions = [...mockInventoryTransactions];

      if (_params.itemId) {
        filteredTransactions = filteredTransactions.filter(
          (t) => t.itemId === _params.itemId
        );
      }

      if (_params.transactionType) {
        filteredTransactions = filteredTransactions.filter(
          (t) => t.transactionType === _params.transactionType
        );
      }

      if (_params.paymentStatus) {
        filteredTransactions = filteredTransactions.filter(
          (t) => t.paymentStatus === _params.paymentStatus
        );
      }

      if (_params.fromDate) {
        const fromDate = new Date(_params.fromDate);
        filteredTransactions = filteredTransactions.filter(
          (t) => new Date(t.transactionDate) >= fromDate
        );
      }

      if (_params.toDate) {
        const toDate = new Date(_params.toDate);
        filteredTransactions = filteredTransactions.filter(
          (t) => new Date(t.transactionDate) <= toDate
        );
      }

      // Pagination
      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 10;
      const startIndex = (page - 1) * limit;
      const paginatedTransactions = filteredTransactions.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedTransactions, count: filteredTransactions.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.itemId && { itemId: _params.itemId }),
        ...(_params.transactionType && {
          transactionType: _params.transactionType,
        }),
        ...(_params.paymentStatus && { paymentStatus: _params.paymentStatus }),
        ...(_params.fromDate && { fromDate: _params.fromDate }),
        ...(_params.toDate && { toDate: _params.toDate }),
        ...(_params.sort && { sort: _params.sort }),
        ...(_params.order && { order: _params.order }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseInventoryPath}/transactions`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        if (body && (Array.isArray(body.data) || body.data === undefined)) {
          return normalizePaginatedResponse<TInventoryTransaction>(body, 'data');
        }
        return normalizePaginatedResponse<TInventoryTransaction>({}, 'data');
      })
      .catch((error) => {
        console.error('Error fetching inventory transactions:', error);
        return normalizePaginatedResponse<TInventoryTransaction>({}, 'data');
      });
  };

  const getInventoryTransactionById = (
    id: string
  ): Promise<TInventoryTransaction> => {
    if (withMockData) {
      const transaction = mockInventoryTransactions.find((t) => t.id === id);
      if (transaction) {
        return Promise.resolve(transaction);
      }
      return Promise.reject(new Error('Transaction not found'));
    }

    return httpAPI.admin.get<TInventoryTransaction>(
      `${baseInventoryPath}/transactions/${id}`
    );
  };

  const createInventoryTransaction = (
    transaction: Omit<TInventoryTransaction, 'id' | 'totalAmount'>
  ): Promise<TInventoryTransaction> => {
    if (withMockData) {
      const newTransaction: TInventoryTransaction = {
        ...transaction,
        id: `trans-${Date.now()}`,
        totalAmount: transaction.quantity * transaction.unitPrice,
      };
      return Promise.resolve(newTransaction);
    }

    return httpAPI.admin.post<TInventoryTransaction>(
      `${baseInventoryPath}/transactions`,
      transaction
    );
  };

  const updateInventoryTransaction = (
    id: string,
    transaction: Partial<TInventoryTransaction>
  ): Promise<TInventoryTransaction> => {
    if (withMockData) {
      const existingTransaction = mockInventoryTransactions.find(
        (t) => t.id === id
      );
      if (existingTransaction) {
        const updatedTransaction = { ...existingTransaction, ...transaction };
        if (
          transaction.quantity !== undefined ||
          transaction.unitPrice !== undefined
        ) {
          updatedTransaction.totalAmount =
            updatedTransaction.quantity * updatedTransaction.unitPrice;
        }
        return Promise.resolve(updatedTransaction);
      }
      return Promise.reject(new Error('Transaction not found'));
    }

    return httpAPI.admin.put<TInventoryTransaction>(
      `${baseInventoryPath}/transactions/${id}`,
      transaction
    );
  };

  const deleteInventoryTransaction = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(
      `${baseInventoryPath}/transactions/${id}`
    );
  };

  // ===================== INVENTORY CATEGORIES =====================

  const getInventoryCategoryList = (
    _params: TInventoryCategoryParams
  ): Promise<TInventoryCategoryListApiResponse> => {
    if (withMockData) {
      let filteredCategories = [...mockInventoryCategories];

      if (_params.isActive !== undefined) {
        filteredCategories = filteredCategories.filter(
          (c) => c.isActive === _params.isActive
        );
      }

      if (_params.name) {
        const searchTerm = _params.name.toLowerCase();
        filteredCategories = filteredCategories.filter((c) =>
          c.name.toLowerCase().includes(searchTerm)
        );
      }

      // Pagination
      const page = Number(_params.page) || 1;
      const limit = Number(_params.limit) || 10;
      const startIndex = (page - 1) * limit;
      const paginatedCategories = filteredCategories.slice(
        startIndex,
        startIndex + limit
      );

      return Promise.resolve(
        normalizePaginatedResponse(
          { data: paginatedCategories, count: filteredCategories.length, page, limit },
          'data'
        )
      );
    }

    const requestObj = {
      params: {
        page: Number(_params.page) || 1,
        limit: Number(_params.limit) || 10,
        ...(_params.isActive !== undefined && { isActive: _params.isActive }),
        ...(_params.name && { name: _params.name }),
        ...(_params.sort && { sort: _params.sort }),
        ...(_params.order && { order: _params.order }),
      },
    };

    return httpAPI.admin
      .get<any>(`${baseInventoryPath}/categories`, requestObj)
      .then((res) => {
        const body = res?.data ?? res;
        if (body && (Array.isArray(body.data) || body.data === undefined)) {
          return normalizePaginatedResponse<TInventoryCategory>(body, 'data');
        }
        return normalizePaginatedResponse<TInventoryCategory>({}, 'data');
      })
      .catch((error) => {
        console.error('Error fetching inventory categories:', error);
        return normalizePaginatedResponse<TInventoryCategory>({}, 'data');
      });
  };

  const getInventoryCategoryById = (
    id: string
  ): Promise<TInventoryCategory> => {
    if (withMockData) {
      const category = mockInventoryCategories.find((c) => c.id === id);
      if (category) {
        return Promise.resolve(category);
      }
      return Promise.reject(new Error('Category not found'));
    }

    return httpAPI.admin.get<TInventoryCategory>(
      `${baseInventoryPath}/categories/${id}`
    );
  };

  const createInventoryCategory = (
    category: Omit<TInventoryCategory, 'id' | 'totalItems' | 'totalValue'>
  ): Promise<TInventoryCategory> => {
    if (withMockData) {
      const newCategory: TInventoryCategory = {
        ...category,
        id: `cat-${Date.now()}`,
        totalItems: 0,
        totalValue: 0,
      };
      return Promise.resolve(newCategory);
    }

    return httpAPI.admin.post<TInventoryCategory>(
      `${baseInventoryPath}/categories`,
      category
    );
  };

  const updateInventoryCategory = (
    id: string,
    category: Partial<TInventoryCategory>
  ): Promise<TInventoryCategory> => {
    if (withMockData) {
      const existingCategory = mockInventoryCategories.find((c) => c.id === id);
      if (existingCategory) {
        return Promise.resolve({ ...existingCategory, ...category });
      }
      return Promise.reject(new Error('Category not found'));
    }

    return httpAPI.admin.put<TInventoryCategory>(
      `${baseInventoryPath}/categories/${id}`,
      category
    );
  };

  const deleteInventoryCategory = (id: string): Promise<void> => {
    if (withMockData) {
      return Promise.resolve();
    }

    return httpAPI.admin.delete<void>(`${baseInventoryPath}/categories/${id}`);
  };

  return {
    // Items
    getInventoryItemList,
    getInventoryItemById,
    createInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    // Transactions
    getInventoryTransactionList,
    getInventoryTransactionById,
    createInventoryTransaction,
    updateInventoryTransaction,
    deleteInventoryTransaction,
    // Categories
    getInventoryCategoryList,
    getInventoryCategoryById,
    createInventoryCategory,
    updateInventoryCategory,
    deleteInventoryCategory,
  };
};
